// 2. External libraries
import axios from 'axios';
import { AxiosHeaders } from 'axios';
import type { AxiosRequestConfig } from 'axios';

// 5. Custom hooks and utilities
import { getResolvedApiBaseUrl } from '~/config';

/**
 * Single shared transport channel for every generated API call.
 *
 * A module-level axios instance carries the auth-aware request flow. Generated
 * hooks route through `apiMutator`, which forwards to this instance, so auth and
 * base URL live in exactly one place. The base URL and token are bound per
 * request (not at instance-creation time) so they pick up the resolved config
 * and the freshly minted session token instead of build-time defaults.
 */
export const apiClient = axios.create();

/**
 * Module-level token getter. Returns the current auth token (or null when signed
 * out / before the bridge registers a getter). Defaults to a getter that yields
 * null so requests made before the bridge mounts omit the Authorization header.
 */
let authTokenGetter: () => Promise<string | null> = () => Promise.resolve(null);

/**
 * Register the async token getter used by the mutator per request.
 *
 * The auth bridge calls this with a getter that resolves the current Clerk
 * session token. Because the mutator awaits this getter on every request, the
 * first authenticated request after a cold load still carries a token even
 * though the value is not available synchronously at request time.
 */
export function setAuthTokenGetter(getter: () => Promise<string | null>): void {
  authTokenGetter = getter;
}

/**
 * Set the Authorization header on a request config, using AxiosHeaders methods
 * when available and falling back to property access otherwise.
 *
 * `config.headers` may be a plain object or an `AxiosHeaders` instance; the
 * latter must be mutated via `set`/`delete` to behave consistently.
 */
function applyAuthHeader(config: AxiosRequestConfig, token: string | null): void {
  const headers = config.headers;

  if (headers instanceof AxiosHeaders) {
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    } else {
      headers.delete('Authorization');
    }
    return;
  }

  if (token) {
    config.headers = { ...headers, Authorization: `Bearer ${token}` };
  } else if (headers) {
    const next = { ...headers };
    delete next.Authorization;
    config.headers = next;
  }
}

/**
 * orval custom mutator. Resolves the base URL and auth token per request, then
 * forwards the merged config to the shared axios instance and returns
 * `response.data` (typed `T`), never the full response.
 *
 * Late-binding the base URL ensures preview/production requests target the
 * resolved API host rather than the localhost default captured at module load.
 * Awaiting the token getter ensures the first authenticated request after a cold
 * load carries the Authorization header.
 */
export async function apiMutator<T>(
  config: AxiosRequestConfig,
  options?: AxiosRequestConfig
): Promise<T> {
  const merged: AxiosRequestConfig = { ...config, ...options };
  merged.baseURL = getResolvedApiBaseUrl();

  const token = await authTokenGetter();
  applyAuthHeader(merged, token);

  const response = await apiClient.request<T>(merged);
  return response.data;
}
